import { Box, List, ListItem, ListItemIcon, ListItemText, Typography, Checkbox, Chip } from '@mui/material';
import MapLayer from 'src/icons/MapLayer';
import Peoples from 'src/icons/Peoples';
import { AIIcon } from 'src/icons/AIIcon';

import { getWordWithNumber } from 'src/utils';

import { useFormContext } from 'react-hook-form';

import type { ICollectionsChatBundles } from 'src/types/Collections';

type CollectionsListProps = {
    list: ICollectionsChatBundles;
    isDisabled?: boolean;
    recommendedIds?: number[];
};

const EmptyList = () => {
    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            flexGrow={10}
        >
            <Box mb={1}>
                <MapLayer />
            </Box>
            <Box mb={0.5}>
                <Typography
                    variant="body1"
                    color="text.primary"
                    textAlign="center"
                >
                    Подборки не найдены
                </Typography>
            </Box>
            <Box>
                <Typography
                    variant="body2"
                    color="secondary"
                    textAlign="center"
                >
                    Попробуйте изменить запрос или выбрать другой фильтр
                </Typography>
            </Box>
        </Box>
    );
};

export default function CollectionsList({ list, isDisabled, recommendedIds = [] }: CollectionsListProps) {
    const { register, watch, setValue } = useFormContext();
    const collections: string[] = watch('collections');

    // Сортируем по алфавиту, но не перемещаем выбранные в начало
    const sortedList = [...list].sort((a, b) => a.name.localeCompare(b.name, 'ru'));

    if (list.length < 1) {
        return <EmptyList />;
    }

    return (
        <Box>
            <List
                sx={{
                    width: '100%',
                    background: 'var(--lm-surface)',
                    border: '1px solid',
                    borderColor: 'var(--lm-border)',
                    display: 'block',
                    borderRadius: '16px',
                }}
                disablePadding
            >
                {sortedList.map(({ name, city, location, chat_count, id }, idx) => {
                    const checked = collections.includes(String(id));

                    const listItem = (
                        <ListItem
                            secondaryAction={
                                <Checkbox
                                    edge="end"
                                    color="primary"
                                    id={String(id)}
                                    disabled={isDisabled}
                                    value={String(id)}
                                    checked={checked}
                                    {...register('collections')}
                                />
                            }
                            divider={idx !== sortedList.length - 1}
                        >
                            <ListItemIcon>
                                <MapLayer />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <Typography
                                            variant="body1"
                                            color="text.primary"
                                        >
                                            {name}
                                        </Typography>
                                        {recommendedIds.includes(id) && (
                                            <Chip
                                                icon={
                                                    <AIIcon
                                                        width={14}
                                                        height={14}
                                                    />
                                                }
                                                label="ИИ рекомендует"
                                                size="small"
                                                sx={{
                                                    backgroundColor: 'rgba(122, 48, 228, 0.1)',
                                                    color: '#7A30E4',
                                                    fontWeight: 600,
                                                    border: '1px solid rgba(122, 48, 228, 0.2)',
                                                    '& .MuiChip-icon': {
                                                        color: '#7A30E4',
                                                    },
                                                }}
                                            />
                                        )}
                                    </Box>
                                }
                                secondary={
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        {(city || location) && (
                                            <Typography
                                                variant="body2"
                                                color="text.primary"
                                                sx={{
                                                    fontWeight: 500,
                                                    backgroundColor: 'rgba(0, 136, 204, 0.1)',
                                                    padding: '2px 8px',
                                                    borderRadius: '12px',
                                                    border: '1px solid rgba(0, 136, 204, 0.2)',
                                                }}
                                            >
                                                {city || location}
                                            </Typography>
                                        )}

                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <Peoples />
                                            <Typography
                                                variant="body2"
                                                color="text.primary"
                                            >
                                                {getWordWithNumber(chat_count, ['чат', 'чата', 'чатов'])}
                                            </Typography>
                                        </Box>
                                    </Box>
                                }
                            />
                        </ListItem>
                    );

                    return (
                        <label
                            key={id}
                            htmlFor={String(id)}
                            style={{
                                cursor: 'pointer',
                            }}
                        >
                            {listItem}
                        </label>
                    );
                })}
            </List>
        </Box>
    );
}
